import React, { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { Label, Spinner } from 'reactstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { ApplicationState } from 'store/redux';
import * as AuthenticationStore from 'store/redux/Authentication';
import { IAuthenticationActionCreators, UserState } from 'store/redux/Authentication';
import Context from 'configuration/context';
import { conversationServiceFactory, IMessageRecipient } from 'services/conversationService';
import { authenticationServiceFactory } from 'services/authenticationService';
import useConversations from 'hooks/useConversations';
import {
  ControlledFileUploadInput,
  ControlledSelect,
  ControlledTextArea,
  ControlledTextInput,
} from 'components/form';
import './Conversations.css';
import { IUserInRole } from './Login';
import { downloadAttachment } from 'helpers/downloadAttachment';

export type IConversationsProps = IAuthenticationActionCreators & {
  authenticationState?: UserState;
};

export interface IConversationModel {
  subject: string;
  recipient: IMessageRecipient;
  message: string;
  attachments: File[];
}

const Conversations: React.FunctionComponent<React.PropsWithChildren<IConversationsProps>> = ({
  authenticationState,
  setLoginState,
}: React.PropsWithChildren<IConversationsProps>) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const { conversationId } = useParams();

  const customerId: string | undefined =
    authenticationState?.UserInRole?.selectedCustomer?.customerId;
  const conversationService = conversationServiceFactory(
    Context.APIUrl + '/v1',
    authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState)
  );

  const {
    recipients,
    conversationsAreLoading,
    conversationIsLoading,
    conversations,
    loadConversation,
    selectedConversation,
    addMessageToConversation,
    isSubmitting,
    createConversation,
    errorMessage,
    toggleArchivingStatus,
  } = useConversations(customerId || '-1', conversationId || '-1', conversationService);

  const LoginState = authenticationState?.LoginState;

  const [showArchivedConversations, setShowArchivedConversations] = useState(false);

  const showNewConversationForm = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    navigate('/contact/conversation');
    reset({});
  };

  const toggleShowArchivedConversationsVisibility = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setShowArchivedConversations(!showArchivedConversations);
  };

  const showArchivedConversationTogglerClass = showArchivedConversations
    ? 'fa fa-toggle-on'
    : 'fa fa-toggle-off';
  const conversationActivationTogglerClass =
    selectedConversation && selectedConversation.isArchived
      ? 'fa fa-toggle-on'
      : 'fa fa-toggle-off';
  const conversationArchivingTogglerLabel =
    selectedConversation && selectedConversation.isArchived
      ? 'conversation.ARCHIVED'
      : 'conversation.NOT_ARCHIVED';

  const authenticationService = useMemo(() => {
    return authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState);
  }, [authenticationState]);

  const authenticationValidation =
    authenticationState &&
    authenticationState.UserInRole &&
    authenticationState?.LoginState === AuthenticationStore.LoginState.LOGGED_IN &&
    !!selectedConversation?.customerId;

  useEffect(() => {
    if (customerId + '' !== selectedConversation?.customerId + '' && customerId) {
      if (authenticationValidation) {
        switch (authenticationState.UserInRole?.role) {
          case 'avonovaStaff':
            authenticationService
              .getOrRenewNativeToken()
              .then((accessToken) =>
                authenticationService.fetchUserDetails(
                  accessToken,
                  selectedConversation?.customerId
                )
              )
              .then((userInRole: IUserInRole) => {
                setLoginState &&
                  setLoginState({
                    UserInRole: userInRole,
                  });
              });
            break;
          default:
            let customer = !!selectedConversation?.customerId
              ? authenticationState.UserInRole?.availableCustomers?.find(
                  (c) => c.customerId.toString() === selectedConversation?.customerId
                )
              : null;
            if (customer) {
              setLoginState &&
                authenticationState.UserInRole &&
                setLoginState({
                  UserInRole: { ...authenticationState.UserInRole, selectedCustomer: customer },
                });
            }
            break;
        }
      } else {
        setLoginState &&
          setLoginState({
            RequestedCustomerId: selectedConversation?.customerId,
          });
      }
    }

    loadConversation();
  }, [conversationId, selectedConversation?.customerId]);

  useEffect(() => {
    if (
      !!selectedConversation?.customerId &&
      selectedConversation?.customerId !==
        authenticationState?.UserInRole?.selectedCustomer?.customerId &&
      setLoginState
    ) {
      let customers = authenticationState?.UserInRole?.availableCustomers?.filter(
        (c) => c.customerId == selectedConversation.customerId
      );
      if (customers?.length === 1 && authenticationState?.UserInRole?.availableCustomers) {
        let customer = authenticationState?.UserInRole?.availableCustomers?.filter(
          (c) => c.customerId == selectedConversation.customerId
        )[0];
        setLoginState({
          ...authenticationState,
          UserInRole: { ...authenticationState?.UserInRole, selectedCustomer: customer },
        });
      }
    }
  }, [selectedConversation?.conversationId, setLoginState]);

  useEffect(() => {
    if (!conversationId) {
      // New conversation page: clear everything
      conversationForm.reset(
        { subject: '', message: '', recipient: undefined, attachments: [] },
        { keepDefaultValues: true }
      );
    } else {
      // Existing conversation page: clear the inline message composer
      reset({ message: '', attachments: [] }, { keepDefaultValues: true });
    }
  }, [customerId, conversationId]);

  const { reset, watch, handleSubmit, control, setValue } = useForm<IConversationModel>({
    defaultValues: { attachments: [] },
    mode: 'all',
  });
  const conversationForm = useForm<IConversationModel>({
    defaultValues: { attachments: [] },
    mode: 'all',
  });

  let messageFormModel: IConversationModel = watch();
  let conversationFormModel: IConversationModel = conversationForm.watch();

  const onMessageSubmit = (form: IConversationModel) => {
    if (
      LoginState &&
      LoginState === AuthenticationStore.LoginState.LOGGED_IN &&
      selectedConversation
    ) {
      addMessageToConversation(form, () => reset());
    }
  };

  const onConversationSubmit = (form: IConversationModel) => {
    if (LoginState && LoginState === AuthenticationStore.LoginState.LOGGED_IN && customerId) {
      createConversation(form, () => {
        conversationForm.reset(
          { attachments: [], message: '', subject: '', recipient: undefined },
          { keepDefaultValues: true }
        );
        messageListRef.current && messageListRef.current.scrollIntoView({ behavior: 'smooth' });
      });
    }
  };

  const toggleArchivingForSelectedConversation = (event: any) => {
    event.preventDefault();
    selectedConversation && toggleArchivingStatus();
  };

  const removeMessageAttachment = (index: number) => {
    setValue(
      'attachments',
      messageFormModel.attachments.filter((i, _index) => {
        return index !== _index;
      })
    );
  };

  const removeConversationAttachment = (index: number) => {
    conversationForm.setValue(
      'attachments',
      conversationFormModel.attachments.filter((i, _index) => {
        return index !== _index;
      })
    );
  };

  const downloadItem = (attachment, conversationId) => {
    if (LoginState && LoginState === AuthenticationStore.LoginState.LOGGED_IN) {
      downloadAttachment(
        conversationId,
        attachment.fileName,
        'conversation',
        attachment.attachmentId,
        authenticationState,
        setLoginState
      );
    }
  };

  const messageListRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={messageListRef}>
      <div className="container mt-4">
        <div className="row limited">
          <div className="col-12 col-lg-8 px-4">
            {!!errorMessage && <div className="error-message">{t(errorMessage)}</div>}
            {conversationId && !errorMessage && (
              <>
                {conversationIsLoading ? (
                  <div className="conversation-loader">
                    {t('conversation.FETCHING_CONVERSATIONS')}
                  </div>
                ) : (
                  <>
                    <div className="conversation-form row">
                      {isSubmitting && (
                        <div
                          className="conversation-submission-overlay"
                          onClick={(event: any) => {
                            event.preventDefault();
                            event.stopPropagation();
                            return false;
                          }}
                        ></div>
                      )}
                      <div className="col-12">
                        <h3 className="conversation-title">{selectedConversation?.subject}</h3>
                        <a
                          href="/"
                          className="conversation-archiver"
                          onClick={toggleArchivingForSelectedConversation}
                        >
                          <i className={conversationActivationTogglerClass}></i>{' '}
                          {t(conversationArchivingTogglerLabel)}
                        </a>
                      </div>
                      <form
                        name="add-message-form"
                        className="col-12"
                        onSubmit={handleSubmit((form, event) => {
                          event && event.preventDefault();
                          event && event.stopPropagation();
                          onMessageSubmit(form);
                        })}
                      >
                        <div className="row">
                          <Label className="col-12">
                            <ControlledTextArea<IConversationModel>
                              readonly={isSubmitting}
                              rules={{ required: true }}
                              control={control}
                              fieldName="message"
                              className="message-text-area"
                              placeholder={t('conversation.FIELD_PLACEHOLDER_MESSAGE')}
                              label={t('conversation.FIELD_NAME_MESSAGE')}
                            />
                            <span className="field-description">
                              {t('conversation.FIELD_DESCRIPTION_MESSAGE')}
                            </span>
                          </Label>
                          <div className="col-md-6">
                            <Label>
                              <ControlledFileUploadInput<IConversationModel>
                                className="hidden"
                                control={control}
                                fieldName="attachments"
                                placeholder={t('conversation.BUTTON_LABEL_ATTACH_FILES')}
                              />
                              <div className="pill link">
                                <i className="fa fa-plus-circle"></i>{' '}
                                {t('conversation.BUTTON_LABEL_ATTACH_FILES')}
                              </div>
                            </Label>
                            {messageFormModel.attachments &&
                              !!messageFormModel.attachments.length && (
                                <>
                                  <br />
                                  <Label>{t('conversation.FIELD_NAME_ATTACHMENTS')}</Label>
                                  <ul>
                                    {messageFormModel.attachments.map(
                                      (attachment: File, index: number) => (
                                        <li key={index}>
                                          {attachment.name}{' '}
                                          <i
                                            onClick={() => {
                                              removeMessageAttachment(index);
                                            }}
                                            title={t('conversation.TITLE_REMOVE_ATTACHMENT')}
                                            className="fa fa-minus-circle remove-attachment-btn"
                                          ></i>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </>
                              )}
                          </div>
                          <div className="col-md-6">
                            <button
                              className="conversation-tool pill-primary link submit-button"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <span>
                                  <Spinner size="sm" /> {t('conversation.SUBMITTING')}
                                </span>
                              ) : (
                                <span>
                                  <i className="fa fa-plus-circle"></i> {t('conversation.SUBMIT')}
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                    <h4 className="row">{t('conversation.MESSAGES')}</h4>
                    <ul className="message-list row">
                      {selectedConversation?.messages.map((message, index) => (
                        <li className="message-item col-12" key={index}>
                          <span className="message-header">
                            {dayjs(message.creationDate).format('YY/MM/DD HH:mm')}{' '}
                            {message.authorIsCurrentUser ? t('conversation.YOU') : message.author}{' '}
                            {t('conversation.WROTE')}{' '}
                          </span>
                          <span className="message-content">{message.message}</span>
                          {message.attachments.length > 0 && (
                            <span className="message-attachments">
                              {message.attachments.map((attachment) => (
                                <span
                                  key={attachment.filePath}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    downloadItem(attachment, conversationId);
                                  }}
                                  className="attachment"
                                >
                                  {attachment.fileName}
                                </span>
                              ))}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}

            {!conversationId && (
              <div className="conversation-form row">
                {isSubmitting && (
                  <div
                    className="conversation-submission-overlay"
                    onClick={(event: any) => {
                      event.preventDefault();
                      event.stopPropagation();
                      return false;
                    }}
                  ></div>
                )}
                <form
                  name="add-conversation-form col-12 mt-4"
                  onSubmit={conversationForm.handleSubmit((form, event) => {
                    event && event.preventDefault();
                    event && event.stopPropagation();
                    onConversationSubmit(form);
                  })}
                >
                  <div className="row">
                    <div className="form-header col-12 mt-4 ">
                      <span className="form-title">{t('conversation.START_NEW_CONVERSATION')}</span>
                      <span className="archived-toggler"></span>
                    </div>
                    <div className="col-12 mt-3">
                      <div className="mb-2">{t('conversation.NEW_CONVERSATION_INFO')}</div>
                      <div>{t('conversation.NEW_CONVERSATION_INFO_RETENTION')}</div>
                    </div>
                    <Label className="col-12 mt-4">
                      <ControlledTextInput<IConversationModel>
                        readonly={isSubmitting}
                        rules={{ required: true }}
                        control={conversationForm.control}
                        fieldName="subject"
                        placeholder={t('conversation.FIELD_PLACEHOLDER_SUBJECT')}
                        label={t('conversation.FIELD_NAME_SUBJECT')}
                      />
                    </Label>
                    <Label className="col-12 mt-4">
                      <ControlledSelect<IConversationModel, IMessageRecipient>
                        readonly={isSubmitting}
                        options={recipients.map((recipient) => {
                          return {
                            label: recipient.displayName + ' <' + recipient.email + '>',
                            value: recipient,
                          };
                        })}
                        rules={{ required: true }}
                        getOptionValue={(recipient) => recipient.value.email}
                        getOptionLabel={(recipient) => recipient.label}
                        control={conversationForm.control}
                        fieldName="recipient"
                        placeholder={t('conversation.FIELD_PLACEHOLDER_RECIPIENT')}
                        label={t('conversation.FIELD_NAME_RECIPIENT')}
                        isOnlyValue
                      />
                    </Label>
                    <Label className="col-12 mt-4">
                      <ControlledTextArea<IConversationModel>
                        readonly={isSubmitting}
                        rules={{ required: true }}
                        control={conversationForm.control}
                        fieldName="message"
                        className="message-text-area"
                        placeholder={t('conversation.FIELD_PLACEHOLDER_MESSAGE')}
                        label={t('conversation.FIELD_NAME_MESSAGE')}
                      />
                      <span className="field-description">
                        {t('conversation.FIELD_DESCRIPTION_MESSAGE')}
                      </span>
                    </Label>
                    <div className="col-md-6">
                      <Label>
                        <ControlledFileUploadInput<IConversationModel>
                          readonly={isSubmitting}
                          className="hidden"
                          control={conversationForm.control}
                          fieldName="attachments"
                          placeholder={t('conversation.BUTTON_LABEL_ATTACH_FILES')}
                        />
                        <div className="pill link">
                          <i className="fa fa-plus-circle"></i>{' '}
                          {t('conversation.BUTTON_LABEL_ATTACH_FILES')}
                        </div>
                      </Label>
                      {conversationFormModel.attachments &&
                        !!conversationFormModel.attachments.length && (
                          <>
                            <br />
                            <Label>{t('conversation.FIELD_NAME_ATTACHMENTS')}</Label>
                            <ul>
                              {conversationFormModel.attachments.map(
                                (attachment: File, index: number) => (
                                  <li key={index}>
                                    {attachment.name}{' '}
                                    <i
                                      onClick={() => {
                                        removeConversationAttachment(index);
                                      }}
                                      title={t('conversation.TITLE_REMOVE_ATTACHMENT')}
                                      className="fa fa-minus-circle remove-attachment-btn"
                                    ></i>
                                  </li>
                                )
                              )}
                            </ul>
                          </>
                        )}
                    </div>
                    <div className="col-md-6">
                      <button
                        className="conversation-tool pill-primary link submit-button"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span>
                            <Spinner size="sm" /> {t('conversation.SUBMITTING')}
                          </span>
                        ) : (
                          <span>
                            <i className="fa fa-plus-circle"></i> {t('conversation.SUBMIT')}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
          <div className="col-12 col-lg-4 px-4 ">
            <h3>{t('conversation.YOUR_CONVERSATIONS')}</h3>
            {!conversationsAreLoading ? (
              <div>
                <a
                  href="/"
                  className="conversation-tool pill-primary link"
                  onClick={showNewConversationForm}
                >
                  <i className="fa fa-plus-circle"></i> {t('conversation.START_NEW_CONVERSATION')}
                </a>
                <a
                  href="/"
                  className="conversation-tool"
                  onClick={toggleShowArchivedConversationsVisibility}
                >
                  <i className={showArchivedConversationTogglerClass}></i>{' '}
                  {t('conversation.SHOW_ARCHIVED_CONVERSATIONS')}
                </a>
                <ul className="conversation-list">
                  {conversations
                    .filter(
                      (conversation) =>
                        !conversation.isArchived ||
                        showArchivedConversations ||
                        conversation.conversationId == selectedConversation?.conversationId
                    )
                    .map((conversation) => {
                      const className: string =
                        conversation.conversationId === conversationId
                          ? 'active conversation-summary-card'
                          : 'conversation-summary-card';

                      return (
                        <li
                          className={className}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            navigate('/contact/conversation/' + conversation.conversationId);
                          }}
                          key={conversation.conversationId}
                        >
                          <div className="conversation-meta">
                            <div className="conversation-creation-date">
                              {conversation.creationDate}{' '}
                              {conversation.hasUnreadMessages && <span className="new">NYTT!</span>}
                            </div>
                            <div className="conversation-other-party">
                              {conversation.otherParty[0].displayName}
                            </div>
                          </div>
                          <div className="conversation-subject">
                            {conversation.isArchived && <i className="fa fa-archive"></i>}
                            {conversation.subject}
                          </div>
                        </li>
                      );
                    })}
                </ul>
              </div>
            ) : (
              <div className="conversation-loader">
                <Spinner /> {t('conversation.FETCHING_CONVERSATIONS')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(
  (appState: ApplicationState) => ({ authenticationState: appState.authentication }),
  { ...AuthenticationStore.actions }
)(Conversations);
